import {
  AggregateVariable,
  AppDomainsAggregatesModelsOrderByVariable,
  type GetInteractionAggregatesReqAdditionalGroupBysItem,
  type GetInteractionAggregatesReqFiltersV2Item,
  type GetInteractionAggregatesReqGroupBy,
  type QueryOrdering,
  TimeRangeKind,
  type TimeRangeConfig,
} from '@/api/generated/model';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { getTimezoneName } from '@/utils/date.util';

export const MODEL_SAVINGS_SUMMARY_VARIABLES: AggregateVariable[] = [
  AggregateVariable.potential_savings,
  AggregateVariable.scaled_cost,
  AggregateVariable.n_interactions,
  AggregateVariable.most_used_provider,
  AggregateVariable.most_used_modality,
];

export const MODEL_SAVINGS_TASK_VARIABLES: AggregateVariable[] = [
  AggregateVariable.potential_savings,
  AggregateVariable.scaled_cost,
  AggregateVariable.n_interactions,
  AggregateVariable.most_used_provider,
  AggregateVariable.most_used_modality,
];

export const MODEL_SAVINGS_TIER_MIX_VARIABLES: AggregateVariable[] = [AggregateVariable.scaled_cost];

export const MODEL_SAVINGS_SEGMENT_VARIABLES: AggregateVariable[] = [AggregateVariable.potential_savings];

export const MODEL_SAVINGS_TOP_TASKS_CARD_LIMIT = 3;
export const MODEL_SAVINGS_TOP_FIVE_TASKS_LIMIT = 5;
export const MODEL_SAVINGS_TOP_OPPORTUNITIES_LIMIT = 10;
export const MODEL_SAVINGS_DRAWER_PAGE_SIZE = MODEL_SAVINGS_TOP_OPPORTUNITIES_LIMIT;
/** Max tier rows per topic from actual_tier / optimal_tier additional group_bys. */
export const MODEL_SAVINGS_TIER_COUNT = 4;

/** Topic × tier queries return one row per tier; scale pagination so every topic on the page is covered. */
export function modelSavingsTierMixPagination(topicLimit: number, topicOffset: number) {
  return {
    limit: topicLimit * MODEL_SAVINGS_TIER_COUNT,
    offset: topicOffset * MODEL_SAVINGS_TIER_COUNT,
  };
}

export const MODEL_SAVINGS_TASKS_ORDER_BY: QueryOrdering = {
  by: AppDomainsAggregatesModelsOrderByVariable.potential_savings,
  direction: 'desc',
};

export function buildModelSavingsAnnualTimeRange(
  now: Date = new Date(),
  effectiveNow?: Date,
): TimeRangeConfig | undefined {
  return resolveNebulyPageTimeRange(
    {
      kind: TimeRangeKind['12_months'],
      start: '',
      end: '',
      timezone: getTimezoneName(),
      recompute: true,
    },
    now,
    effectiveNow,
  );
}

export function buildModelSavingsTopicGroupBy(): GetInteractionAggregatesReqGroupBy {
  return { kind: 'topic' } as GetInteractionAggregatesReqGroupBy;
}

export function buildModelSavingsActualTierGroupBy(): GetInteractionAggregatesReqGroupBy {
  return { kind: 'actual_tier' } as GetInteractionAggregatesReqGroupBy;
}

export function buildModelSavingsOptimalTierGroupBy(): GetInteractionAggregatesReqGroupBy {
  return { kind: 'optimal_tier' } as GetInteractionAggregatesReqGroupBy;
}

export function buildModelSavingsActualTierAdditionalGroupBy(): GetInteractionAggregatesReqAdditionalGroupBysItem {
  return { kind: 'actual_tier' } as GetInteractionAggregatesReqAdditionalGroupBysItem;
}

export function buildModelSavingsOptimalTierAdditionalGroupBy(): GetInteractionAggregatesReqAdditionalGroupBysItem {
  return { kind: 'optimal_tier' } as GetInteractionAggregatesReqAdditionalGroupBysItem;
}

export function buildModelSavingsTopicFilter(topicName: string): GetInteractionAggregatesReqFiltersV2Item {
  return {
    kind: 'topic',
    values: [topicName],
  } as GetInteractionAggregatesReqFiltersV2Item;
}

export type ModelSavingsTasksQueryScope = {
  projectId: string;
  timeRange: TimeRangeConfig;
  scopeFilters: GetInteractionAggregatesReqFiltersV2Item[];
};
